import React, { useState } from 'react';
import { Button, Checkbox, FormControlLabel, Grid, Paper, Tooltip } from '@mui/material';
import Typography from '@mui/material/Typography';
import { Add } from '@mui/icons-material';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useTranslation } from 'react-i18next';
import { AccountData, CatalogCategoryOut, CatalogService } from '../api';
import { useAuth } from '../session/InternalAuthProvider';
import { useLanguage } from '../common/GeneralUtils';
import { UseAuthPermissions } from '../session/UseAuthPermissions';
import { useApiResponseHandlerContext } from '../common/apiNotifications/useApiResponseHandlerContext';
import { ApiResponseHandlerType } from '../common/apiNotifications/ApiResponseHandlerContext';
import RotatingLoader from '../common/ui/RotatingLoader';
import { useCatalogCategories } from './swr/useCatalogCategories';
import { applyCategoryReorder } from './catalogUtils';
import { SortableCategoryRow } from './SortableCategoryRow';
import { EditCategoryDialog } from './EditCategoryDialog';
import { mb } from '../common/theme/mbTokens';

export const CatalogCategoriesTab = () => {
  const { t } = useTranslation();
  // @ts-ignore
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const language = useLanguage();
  const { readOnly } = UseAuthPermissions();
  const { showResponseNotification } = useApiResponseHandlerContext();
  const { categories, isLoading, mutate } = useCatalogCategories();
  const [showArchived, setShowArchived] = useState(false);
  const [editDialogIsOpen, setEditDialogIsOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<CatalogCategoryOut | undefined>(undefined);

  const activeCategories = categories.filter((category) => !category.archived);
  const archivedCategories = categories.filter((category) => category.archived);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }
    const { reordered, changes } = applyCategoryReorder(activeCategories, active.id as string, over.id as string);
    if (!changes.length) {
      return;
    }
    await mutate([...reordered, ...archivedCategories], { revalidate: false });
    try {
      await Promise.all(
        changes.map((category) =>
          CatalogService.updateCatalogCategory({
            id: category.id,
            label: category.label,
            sort_order: category.sort_order,
            order_page_ids: category.order_page_ids ?? [],
          }),
        ),
      );
    } catch (error: any) {
      showResponseNotification(error?.body?.detail || t('api.error.default'), ApiResponseHandlerType.ERROR);
    } finally {
      await mutate();
    }
  };

  const openDialog = (category?: CatalogCategoryOut) => {
    setSelectedCategory(category);
    setEditDialogIsOpen(true);
  };

  if (isLoading) {
    return (
      <Grid container justifyContent="center" alignItems="center" minHeight="200px">
        <RotatingLoader />
      </Grid>
    );
  }

  return (
    <Grid container flexDirection="column" flexWrap="nowrap" sx={{ maxWidth: 732, p: mb.layout.cardPadding }}>
      <Grid container justifyContent="space-between" alignItems="center" mb={2}>
        <FormControlLabel
          control={<Checkbox size="small" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />}
          label={<Typography variant="body2">{t('catalog.showArchived')}</Typography>}
        />
        <Tooltip title={readOnly ? t('noPermissions') : undefined}>
          <span>
            <Button
              disabled={readOnly}
              variant="contained"
              startIcon={<Add />}
              sx={{ textTransform: 'none' }}
              onClick={() => openDialog(undefined)}>
              {t('catalog.addCategory')}
            </Button>
          </span>
        </Tooltip>
      </Grid>
      {activeCategories.length === 0 ? (
        <Paper variant="outlined" sx={{ padding: 3, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            {t('catalog.noCategoriesYet')}
          </Typography>
        </Paper>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext
            items={activeCategories.map((category) => category.id)}
            strategy={verticalListSortingStrategy}>
            {activeCategories.map((category) => (
              <SortableCategoryRow
                key={category.id}
                category={category}
                account={account}
                language={language}
                readOnly={readOnly}
                onEdit={() => openDialog(category)}
              />
            ))}
          </SortableContext>
        </DndContext>
      )}
      {showArchived && archivedCategories.length > 0 && (
        <Grid container flexDirection="column" mt={2} sx={{ opacity: 0.55 }}>
          <Typography variant="subtitle2" mb={1}>
            {t('catalog.archived')}
          </Typography>
          {archivedCategories.map((category) => (
            <SortableCategoryRow
              key={category.id}
              category={category}
              account={account}
              language={language}
              readOnly
              onEdit={() => openDialog(category)}
            />
          ))}
        </Grid>
      )}
      {editDialogIsOpen && (
        <EditCategoryDialog
          isOpen={editDialogIsOpen}
          onClose={() => setEditDialogIsOpen(false)}
          category={selectedCategory}
          nextSortOrder={activeCategories.length}
          onSaved={() => mutate()}
        />
      )}
    </Grid>
  );
};
